import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useDispatch } from 'react-redux';
import * as Sentry from '@sentry/nextjs';
import buildConfig from 'app/utils/buildConfig';
import AuthActions from 'app/entity/auth/authRedux';
import { sessionIdentifier } from 'app/constants/auth';
import Cookies from 'js-cookie';
import baseApi from 'app/api/base';

const KeycloakCallback = () => {
  const router = useRouter();
  const dispatch = useDispatch();
  const [status, setStatus] = useState('processing'); // 'processing', 'success', 'error'
  const [message, setMessage] = useState('Completing login with SingSaver...');

  useEffect(() => {
    let isProcessing = false;

    const cleanupStorage = () => {
      sessionStorage.removeItem('singsaver_code_verifier');
    };

    const processCallback = async () => {
      // Prevent duplicate processing
      if (isProcessing) {
        return;
      }

      try {
        isProcessing = true;

        // Get parameters from URL
        const { code, state, error } = router.query;

        if (error) {
          console.error('OAuth authorization error:', error);

          Sentry.captureException(new Error(`Keycloak OAuth error: ${error}`), {
            tags: {
              operation: 'keycloak_callback',
              stage: 'oauth_authorization',
            },
            extra: {
              error,
              state,
              query: router.query,
            },
          });

          setStatus('error');
          setMessage('Authorization failed. Please try again.');
          cleanupStorage();
          setTimeout(() => router.push('/'), 3000);
          return;
        }

        if (!code) {
          console.error('No authorization code received');

          Sentry.captureException(
            new Error('No authorization code received from Keycloak'),
            {
              tags: {
                operation: 'keycloak_callback',
                stage: 'code_validation',
              },
              extra: {
                query: router.query,
              },
            },
          );

          setStatus('error');
          setMessage('No authorization code received. Please try again.');
          cleanupStorage();
          setTimeout(() => router.push('/'), 3000);
          return;
        }

        // Retrieve code_verifier from sessionStorage
        const codeVerifier = sessionStorage.getItem('singsaver_code_verifier');

        if (!codeVerifier) {
          console.warn('Code verifier missing from sessionStorage');
        }

        // Prepare request body
        const requestBody = {
          code,
          state,
          code_verifier: codeVerifier, // Snake case
          codeVerifier, // Camel case - try both formats
        };

        // Send authorization code to our backend
        const response = await fetch(
          `${buildConfig.api}v4/auth/keycloak/callback`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify(requestBody),
          },
        );

        const responseText = await response.text();

        let data;
        try {
          data = JSON.parse(responseText);
        } catch (parseError) {
          console.error('Failed to parse response as JSON:', parseError);

          Sentry.captureException(parseError, {
            tags: {
              operation: 'keycloak_callback',
              stage: 'response_parsing',
            },
            extra: {
              responseText,
              response: {
                status: response.status,
                statusText: response.statusText,
              },
            },
          });

          throw new Error('Invalid JSON response from backend');
        }

        if (data.success) {
          // Log successful authentication
          Sentry.addBreadcrumb({
            message: 'Keycloak authentication successful',
            category: 'auth',
            data: {
              hasUserData: !!data.data.user,
              hasTokens: !!data.data.seedly_tokens,
            },
          });

          // Save Seedly tokens in the same format as regular auth
          const tokens = data.data.seedly_tokens;

          const authHeaders = {
            'access-token': tokens['access-token'],
            client: tokens.client,
            uid: tokens.uid,
          };

          // Set headers in API client
          baseApi.setHeaders(authHeaders);

          // Save in cookies (same as other auth methods)
          Cookies.set(sessionIdentifier, JSON.stringify(authHeaders), {
            domain: buildConfig.domain,
            secure: process.env.NEXT_PUBLIC_BUILD_ENV !== 'development',
            expires: 30,
          });

          setStatus('success');
          setMessage('Login successful! Redirecting...');

          // Update Redux state with user data
          if (data.data.user) {
            dispatch(AuthActions.authUserTokenSuccess(data.data.user));
          }

          // Clean up storage and redirect
          cleanupStorage();

          // Small delay to let Redux state update before redirect
          setTimeout(() => {
            window.location.href = '/';
          }, 1500);
        } else {
          console.error('Authentication failed:', data.error);

          Sentry.captureException(
            new Error(`Keycloak authentication failed: ${data.error}`),
            {
              tags: {
                operation: 'keycloak_callback',
                stage: 'backend_authentication',
              },
              extra: {
                response: data,
                code:
                  typeof code === 'string'
                    ? `${code.substring(0, 10)}...`
                    : code, // Log partial code for debugging
              },
            },
          );

          setStatus('error');
          setMessage('Authentication failed. Please try again.');
          cleanupStorage();
          setTimeout(() => router.push('/'), 3000);
        }
      } catch (error) {
        console.error('Network error during callback:', error);

        Sentry.captureException(error, {
          tags: {
            operation: 'keycloak_callback',
            stage: 'network_request',
          },
          extra: {
            query: router.query,
            apiUrl: `${buildConfig.api}v4/auth/keycloak/callback`,
          },
        });

        setStatus('error');
        setMessage('Network error. Please try again.');
        setTimeout(() => router.push('/'), 3000);
      } finally {
        isProcessing = false;
      }
    };

    // Process callback only when router is ready and has query params
    if (router.isReady && router.query.code) {
      processCallback();
    }
  }, [router.isReady, router.query.code, dispatch, router]);

  const getStatusColor = () => {
    switch (status) {
      case 'processing':
        return 'text-blue-500';
      case 'success':
        return 'text-green-500';
      case 'error':
        return 'text-red-500';
      default:
        return 'text-gray-500';
    }
  };

  const getSpinner = () => {
    if (status === 'processing') {
      return (
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4" />
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          {getSpinner()}
          <h2 className="mt-6 text-2xl font-bold text-gray-900">
            SingSaver Login
          </h2>
          <p className={`mt-2 text-sm ${getStatusColor()}`}>{message}</p>
          {status === 'processing' && (
            <div className="mt-4 text-xs text-gray-500">
              <p>✓ Received authorization code</p>
              <p>✓ Verifying with PKCE...</p>
              <p>✓ Creating your Seedly account...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default KeycloakCallback;
